import { ExternalReference } from "@models/ISchema";
import { HERMESLAB_CATEGORY_CODE } from "@services/constants/references";

export interface ExternalReferenceOptionLabels {
  term: string;
  campus: string;
  status: string;
  active: string;
  inactive: string;
}

const SEGMENT_SEPARATOR = " - ";

/**
 * A segment is absent when it is null, undefined, or blank once trimmed.
 * Falsiness is deliberately NOT the test: the status segment derives from
 * `isActive === false`, which must still render (CHS-2861 AC3).
 */
const isPresent = (segment?: string | null): segment is string =>
  segment !== null && segment !== undefined && segment.trim() !== "";

const joinPresent = (segments: (string | null | undefined)[], separator: string): string =>
  segments
    .filter(isPresent)
    .map(segment => segment.trim())
    .join(separator);

/**
 * Join the segments of a selector line, dropping the absent ones so no dangling,
 * doubled, or leading/trailing separator survives (CHS-2861 AC7).
 */
export const joinExternalReferenceSegments = (segments: (string | null | undefined)[]): string =>
  joinPresent(segments, SEGMENT_SEPARATOR);

/**
 * Primary line of a CRN selector result minus its leading CRN (CHS-2861 AC1):
 * `{subject.code}{subject.courseNumber} - {groupNumber} - {subject.name}`.
 *
 * Split out from the full line so the selector can bold the CRN on its own without
 * restating the omission rules.
 *
 * The subject code and the course number are concatenated with no separator between
 * them regardless of either length, and behave as a single segment: whichever is
 * present is rendered, and the segment disappears only when both are missing.
 */
export const getExternalReferenceGroupDetails = (reference?: ExternalReference | null): string => {
  const { groupNumber, subject } = reference ?? {};

  const subjectCode = joinPresent([subject?.code, subject?.courseNumber], "");

  return joinExternalReferenceSegments([subjectCode, groupNumber, subject?.name]);
};

/**
 * Primary line of a CRN selector result (CHS-2861 AC1):
 * `{referenceKey} - {subject.code}{subject.courseNumber} - {groupNumber} - {subject.name}`.
 */
export const getExternalReferencePrimaryLine = (reference?: ExternalReference | null): string =>
  joinExternalReferenceSegments([
    reference?.referenceKey,
    getExternalReferenceGroupDetails(reference),
  ]);

/**
 * Status label of a CRN selector result (CHS-2861 AC3): `true` reads as active,
 * `false` as inactive. Only a null/undefined flag omits the segment.
 */
export const getExternalReferenceStatus = (
  isActive: boolean | null | undefined,
  labels: Pick<ExternalReferenceOptionLabels, "active" | "inactive">,
): string | null => {
  if (isActive === null || isActive === undefined) {
    return null;
  }

  return isActive ? labels.active : labels.inactive;
};

/**
 * Subtitle of a CRN selector result (CHS-2861 AC2):
 * `{term}: {term.code} - {campus}: {campus.code} - {status}: {Active|Inactive}`.
 *
 * Each label travels with its value, so an absent value removes its label too.
 * The campus uses the short key (`MTY`), never the long description.
 *
 * Labels arrive already resolved so this stays a pure helper, free of `t`.
 */
export const getExternalReferenceSubtitle = (
  reference: ExternalReference | null | undefined,
  labels: ExternalReferenceOptionLabels,
): string => {
  const { term, campus, isActive } = reference ?? {};

  const status = getExternalReferenceStatus(isActive, labels);

  return joinExternalReferenceSegments([
    isPresent(term?.code) ? `${labels.term}: ${term.code}` : null,
    isPresent(campus?.code) ? `${labels.campus}: ${campus.code}` : null,
    isPresent(status) ? `${labels.status}: ${status}` : null,
  ]);
};

/**
 * Whether a reference category code is Hermeslab. The backend stores codes lowercased
 * while `referenceCategories` returns them uppercased, so the comparison ignores case.
 */
export const isHermeslabCategoryCode = (code?: string | null): boolean =>
  typeof code === "string" && code.toUpperCase() === HERMESLAB_CATEGORY_CODE;
